import { useState, useCallback, useMemo } from "react";
import {
  useTargetUniverseStats,
  useTargetUniverseFilterOptions,
  useTargetUniverseKanbanData,
  type TargetUniverseFilters,
  type TargetUniverseAccount,
  type KanbanSortBy,
} from "@/hooks/useTargetUniverse";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown } from "lucide-react";

import { TargetUniverseFilterSidebar } from "@/components/targeting/TargetUniverseFilters";
import { TargetUniverseKanban } from "@/components/targeting/TargetUniverseKanban";
import { AccountReviewPanel } from "@/components/targeting/AccountReviewPanel";
import { BulkActionsBar } from "@/components/targeting/BulkActionsBar";
import { TargetUniverseStagesDialog } from "@/components/targeting/TargetUniverseStagesDialog";

const DEFAULT_FILTERS: TargetUniverseFilters = {
  search: "",
  category: [],
  subcategory: [],
  hasContacts: null,
  country: [],
  territory: [],
  carrModality: [],
  modality: [],
  therapeuticArea: [],
  clinicalPhase: [],
  fundingStatus: [],
  employeeRange: [],
  accountTier: [],
  icpTier: [],
  topAccount: false,
  notesStale: false,
  needsScaleReview: false,
};

const TargetUniverse = () => {
  const [filters, setFilters] = useState<TargetUniverseFilters>(DEFAULT_FILTERS);
  const [reviewAccount, setReviewAccount] = useState<TargetUniverseAccount | null>(null);
  const [tierLimits, setTierLimits] = useState<Record<string, number>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<KanbanSortBy>("name");

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const { data: stats } = useTargetUniverseStats();
  const { data: kanbanData, isLoading: kanbanLoading } = useTargetUniverseKanbanData(filters, tierLimits, sortBy);
  const { data: filterOptions } = useTargetUniverseFilterOptions();

  const totalCount = stats?.total_accounts ?? 0;

  const handleFiltersChange = (newFilters: TargetUniverseFilters) => {
    setFilters(newFilters);
    setTierLimits({});
  };

  const handleLoadMore = useCallback((tier: string) => {
    setTierLimits((prev) => ({
      ...prev,
      [tier]: (prev[tier] || 50) + 50,
    }));
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-foreground">Target Universe</h1>
            <TargetUniverseStagesDialog />
          </div>
          <p className="text-sm text-muted-foreground">
            {totalCount.toLocaleString()} accounts · Pre-pipeline command center
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as KanbanSortBy)}>
            <SelectTrigger className="h-8 w-[160px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A→Z)</SelectItem>
              <SelectItem value="contact_count">Contacts (most)</SelectItem>
              <SelectItem value="last_reviewed_at">Last reviewed</SelectItem>
              <SelectItem value="icp_score">ICP score</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Filter Sidebar */}
        <TargetUniverseFilterSidebar
          totalCount={totalCount}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          filterOptions={filterOptions}
          stats={stats ?? null}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <TargetUniverseKanban
            kanbanData={kanbanData}
            isLoading={kanbanLoading}
            onReview={setReviewAccount}
            stats={stats}
            onLoadMore={handleLoadMore}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
          />
        </div>
      </div>

      <BulkActionsBar
        selectedIds={selectedIds}
        onClearSelection={handleClearSelection}
      />

      {/* Review Panel */}
      {reviewAccount && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setReviewAccount(null)}
          />
          <AccountReviewPanel
            account={reviewAccount}
            onClose={() => setReviewAccount(null)}
          />
        </>
      )}
    </div>
  );
};

export default TargetUniverse;
